/**
 * buildingView.ts - Side-view building renderer
 *
 * Each floor has a single room that spans the full width.
 * Props installed in a room are rendered as positioned images inside.
 */

import type { GameStateData, Floor, Room, VisitorReaction } from '../game/state.js';
import { getMaxFloorsForSelectedBuilding, getEmptyRoomImage } from '../game/state.js';
import type { RoomTypeId } from '../game/data/rooms.js';
import { GHOST_DEFS } from '../game/data/ghosts.js';
import { ROOM_TYPES } from '../game/data/rooms.js';
import { CONFIG } from '../game/config.js';
import { t } from '../i18n/index.js';
import { formatSp } from './formatters.js';
import { calcActiveSynergies, type SynergyEffectType } from '../game/data/roomSynergies.js';
import { calcStoryArc } from '../game/storyArc.js';

// Friendly display for synergy effect types
const SYNERGY_EFFECT_ICON: Record<SynergyEffectType, string> = {
  fear_boost:        '👁',
  thrill_boost:      '⚡',
  atmosphere_boost:  '🌫',
  fun_boost:         '🎭',
  scare_multiplier:  '💀',
  visitor_bonus:     '👥',
  sp_bonus:          '💰',
  chain_scare:       '⛓',
};
import type { StoryRole } from '../game/storyArc.js';
import type { RoomSynergyDef } from '../game/data/roomSynergies.js';

/** Open synergy detail popup */
function openSynergyPopup(synergies: RoomSynergyDef[]): void {
  const popupContainer = document.getElementById('popup-container');
  if (!popupContainer) return;

  // Close existing synergy popup if any
  const existing = popupContainer.querySelector('[data-popup-id="synergy-detail"]');
  if (existing) { existing.remove(); return; }

  const overlay = document.createElement('div');
  overlay.className = 'popup-overlay';
  overlay.dataset.popupId = 'synergy-detail';
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });

  const cards = synergies.map((s, idx) => {
    const effectTags = s.effects.map((e) => {
      const icon = SYNERGY_EFFECT_ICON[e.type];
      return `<div class="syn-pop-fx syn-pop-fx-${e.type.replace('_', '-')}"><span class="syn-pop-fx-icon">${icon}</span><span class="syn-pop-fx-label">${t(e.nameKey as Parameters<typeof t>[0])}</span><span class="syn-pop-fx-val">+${e.value}%</span></div>`;
    }).join('');
    return `<div class="syn-pop-card" style="--card-idx:${idx}"><div class="syn-pop-card-glow"></div><div class="syn-pop-card-inner"><div class="syn-pop-head"><span class="syn-pop-rank">${idx + 1}</span><span class="syn-pop-ico">${s.icon}</span><span class="syn-pop-name">${t(s.nameKey as Parameters<typeof t>[0])}</span></div><div class="syn-pop-desc">${t(s.descKey as Parameters<typeof t>[0])}</div><div class="syn-pop-divider"></div><div class="syn-pop-fx-list">${effectTags}</div></div></div>`;
  }).join('');

  overlay.innerHTML = `
    <div class="syn-pop-box">
      <div class="syn-pop-box-border"></div>
      <div class="syn-pop-header">
        <div class="syn-pop-ornament-left"></div>
        <div class="syn-pop-title-wrap"><span class="syn-pop-title-icon">&#x2726;</span><span class="syn-pop-title">${t('synergy_popup_title')}</span><span class="syn-pop-title-icon">&#x2726;</span></div>
        <div class="syn-pop-ornament-right"></div>
      </div>
      <div class="syn-pop-count">${synergies.length} / 20</div>
      <div class="syn-pop-list">${cards}</div>
      <div class="syn-pop-scroll-fade"></div>
    </div>`;
  popupContainer.appendChild(overlay);
}

/*
 * Legacy visitor image mapping (reference only -- sprites are used now):
 *   scaredy_kid: '/images/visitor/kid-walk.webp'
 *   normal: '/images/visitor/normal-walk.webp'
 *   brave: '/images/visitor/brave-walk.webp'
 *   couple: '/images/visitor/couple-walk.webp'
 *   youtuber: '/images/visitor/youtuber-walk.webp'
 *   thrill_seeker: '/images/visitor/thrill-seeker-walk.webp'
 *   coward: '/images/visitor/coward-walk.webp'
 *   exorcist: '/images/visitor/exorcist-walk.webp'
 *   vip: '/images/visitor/vip-walk.webp'
 */

// Room background image mapping (base images per room type)
const ROOM_BG_IMAGES: Record<string, string> = {
  dark_corridor: '/images/room/dark-corridor.webp',
  mirror_room: '/images/room/mirror-room.webp',
  dungeon: '/images/room/dungeon.webp',
  graveyard_room: '/images/room/graveyard.webp',
  basement: '/images/room/basement-school.webp',
  lab: '/images/room/lab.webp',
  library: '/images/room/library.webp',
  attic: '/images/room/attic.webp',
  theater: '/images/room/coffin-room.webp',
  coffin_room: '/images/room/coffin-room.webp',
  doll_room: '/images/room/doll-room.webp',
  kitchen: '/images/room/kitchen.webp',
  bathroom: '/images/room/bathroom.webp',
  // Theme rooms - unique images
  funhouse: '/images/room/funhouse.webp',
  trapeze_room: '/images/room/trapeze-room.webp',
  freak_show: '/images/room/freak-show.webp',
  sacrifice_altar: '/images/room/sacrifice-altar.webp',
  treasure_vault: '/images/room/treasure-vault.webp',
  underground_river: '/images/room/underground-river.webp',
  curse_chamber: '/images/room/curse-chamber.webp',
  mummy_tomb: '/images/room/mummy-tomb.webp',
  captain_cabin: '/images/room/captain-cabin.webp',
  cargo_hold: '/images/room/cargo-hold.webp',
  davy_jones_locker: '/images/room/davy-jones-locker.webp',
  brig: '/images/room/brig.webp',
  crows_nest: '/images/room/crows-nest.webp',
  mine_shaft: '/images/room/mine-shaft.webp',
  crystal_cave: '/images/room/crystal-cave.webp',
  explosives_storage: '/images/room/explosives-storage.webp',
  underground_lake: '/images/room/underground-lake.webp',
  rail_tunnel: '/images/room/rail-tunnel.webp',
  throne_room: '/images/room/throne-room.webp',
  wine_cellar: '/images/room/wine-cellar.webp',
  tower_chamber: '/images/room/tower-chamber.webp',
  server_room: '/images/room/server-room.webp',
  quarantine_zone: '/images/room/quarantine-zone.webp',
  armory: '/images/room/armory.webp',
  witch_hut: '/images/room/witch-hut.webp',
  treehouse: '/images/room/treehouse.webp',
  swamp_path: '/images/room/swamp-path.webp',
  fairy_ring: '/images/room/fairy-ring.webp',
  ancient_ruin: '/images/room/ancient-ruin.webp',
  music_room: '/images/room/music-room.webp',
  science_lab: '/images/room/science-lab.webp',
  morgue: '/images/room/morgue.webp',
  surgery_room: '/images/room/surgery-room.webp',
  secret_passage: '/images/room/secret-passage.webp',
};

// Get room background image with theme awareness
// Falls back to base ROOM_BG_IMAGES if no theme-specific image exists
function getRoomBgImage(roomType: string, themeId: string | undefined): string {
  if (themeId) {
    const themePath = `/images/room/${themeId}/${roomType}.webp`;
    // Check if theme-specific image was already verified to exist
    if (_themeImageCache.has(themePath)) return themePath;
  }
  return ROOM_BG_IMAGES[roomType] ?? '/images/room/dark-corridor.webp';
}

// Cache for verified theme-specific images
const _themeImageCache = new Set<string>();

// Prop image mapping (propId -> image file)
const PROP_IMAGES: Record<string, string> = {
  cobwebs: '/images/props/prop-cobweb.png',
  candles: '/images/props/prop-candle.png',
  blood_pool: '/images/props/prop-blood.png',
  skull_decor: '/images/props/prop-skull.png',
  creaky_floor: '/images/props/prop-creaky-floor.png',
  eerie_painting: '/images/props/prop-painting.png',
  chains: '/images/props/prop-chains.png',
};

// Prop positions within the room (CSS properties)
interface PropPosition {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  width: string;
  transform?: string;
  opacity?: string;
}

const PROP_POSITIONS: Record<string, PropPosition> = {
  cobwebs: { top: '0', left: '0', width: '40px' },
  candles: { bottom: '4px', right: '8px', width: '24px' },
  blood_pool: { bottom: '0', left: '30%', width: '50px' },
  skull_decor: { bottom: '4px', left: '8px', width: '28px' },
  creaky_floor: { bottom: '0', left: '0', width: '100%', opacity: '0.5' },
  eerie_painting: { top: '8px', left: '50%', width: '36px', transform: 'translateX(-50%)' },
  chains: { top: '20%', left: '4px', width: '20px' },
};

export type RoomClickCallback = (floorLevel: number, room: Room | null) => void;

let _prevBuildingRef: GameStateData['building'] | null = null;
let _prevVisitorGroupRef: GameStateData['visitorGroup'] | null = null;
let _prevGhostsRef: GameStateData['ghosts'] | null = null;

export function renderBuilding(
  container: HTMLElement,
  state: GameStateData,
  onRoomClick: RoomClickCallback,
): void {
  // Skip full re-render if building, visitor group, and ghosts haven't changed
  if (
    _prevBuildingRef === state.building &&
    _prevVisitorGroupRef === state.visitorGroup &&
    _prevGhostsRef === state.ghosts
  ) {
    return;
  }
  _prevBuildingRef = state.building;
  _prevVisitorGroupRef = state.visitorGroup;
  _prevGhostsRef = state.ghosts;

  const existingFloors = container.querySelectorAll('.floor');
  const floorsNeeded = state.building.length;

  // Remove extra floors
  while (existingFloors.length > floorsNeeded) {
    existingFloors[existingFloors.length - 1].remove();
  }

  const currentGroup = state.visitorGroup;

  // Calculate story arc roles for all floors
  const storyArc = calcStoryArc(state.building, state);
  const storyRoles = storyArc.roles;

  // Render story arc grade badge at top of building
  const storyBadgeId = 'story-arc-badge';
  let storyBadgeEl = container.querySelector<HTMLElement>(`#${storyBadgeId}`);
  if (storyArc.grade !== 'F') {
    if (!storyBadgeEl) {
      storyBadgeEl = document.createElement('div');
      storyBadgeEl.id = storyBadgeId;
      storyBadgeEl.className = 'story-arc-badge';
      container.insertBefore(storyBadgeEl, container.firstChild);
    }
    storyBadgeEl.textContent = t('story_grade', { grade: storyArc.grade, bonus: storyArc.bonus });
    storyBadgeEl.className = `story-arc-badge grade-${storyArc.grade.toLowerCase()}`;
  } else if (storyBadgeEl) {
    storyBadgeEl.remove();
  }

  // Render synergy summary button (tap to open popup)
  const builtRoomTypes: RoomTypeId[] = state.building
    .map((f) => f.room?.type)
    .filter((rt): rt is RoomTypeId => rt != null);
  const synergies = calcActiveSynergies(builtRoomTypes);

  const synergyBtnId = 'synergy-btn';
  let synergyBtn = container.querySelector<HTMLElement>(`#${synergyBtnId}`);
  if (synergies.length > 0) {
    if (!synergyBtn) {
      synergyBtn = document.createElement('div');
      synergyBtn.id = synergyBtnId;
      synergyBtn.className = 'synergy-btn';
      synergyBtn.addEventListener('click', () => openSynergyPopup(synergies));
      container.insertBefore(synergyBtn, container.firstChild);
    }
    synergyBtn.innerHTML = `<span class="synergy-btn-glow"></span><span class="synergy-btn-icon">⚡</span><span class="synergy-btn-text">${t('synergy_active_count', { n: synergies.length })}</span>`;
  } else if (synergyBtn) {
    synergyBtn.remove();
  }

  // Render floors bottom-up (building is flex-direction: column-reverse)
  state.building.forEach((floor, _floorIdx) => {
    const floorId = `floor-${floor.level}`;
    let floorEl = container.querySelector<HTMLElement>(`#${floorId}`);

    if (!floorEl) {
      floorEl = document.createElement('div');
      floorEl.className = 'floor';
      floorEl.id = floorId;
      container.appendChild(floorEl);
    }

    // Visitor highlight
    const visitorHere = currentGroup &&
      !currentGroup.done &&
      currentGroup.currentFloorIndex === _floorIdx;
    floorEl.classList.toggle('visitor-here', !!visitorHere);

    // Preserve visitor sprites before clearing floor DOM
    const preservedVisitors = Array.from(floorEl.querySelectorAll('.visitor-sprite, .visitor-room-reaction'));
    floorEl.innerHTML = '';

    // Story role label (left side of floor)
    const role = storyRoles[_floorIdx];
    if (role && role !== 'none') {
      const roleLabel = document.createElement('div');
      roleLabel.className = `story-role-label role-${role}`;
      const matched = storyArc.roleMatches[_floorIdx];
      if (matched) roleLabel.classList.add('role-matched');
      roleLabel.textContent = getStoryRoleText(role);
      floorEl.appendChild(roleLabel);
    }

    // Floor label
    const label = document.createElement('div');
    label.className = 'floor-label';
    label.textContent = t('floor_label', { n: floor.level });
    floorEl.appendChild(label);

    // Single room area (takes full width)
    const roomEl = document.createElement('div');
    roomEl.className = 'floor-room';
    floorEl.appendChild(roomEl);

    renderFloorRoom(roomEl, floor, state, onRoomClick);

    // Re-attach preserved visitor sprites into the room element
    for (const el of preservedVisitors) {
      roomEl.appendChild(el);
    }
  });

  // "Add Floor" zone at the top
  const addFloorId = 'add-floor-zone';
  let addFloorEl = container.querySelector<HTMLElement>(`#${addFloorId}`);
  if (!addFloorEl) {
    addFloorEl = document.createElement('div');
    addFloorEl.id = addFloorId;
    addFloorEl.className = 'add-floor-zone';
    container.appendChild(addFloorEl);
  }

  const nextFloorCount = state.building.length + 1;
  const maxFloors = getMaxFloorsForSelectedBuilding(state);
  const canBuildMore = nextFloorCount <= maxFloors;
  const floorCosts = CONFIG.floorCosts;
  const floorCost = nextFloorCount < floorCosts.length
    ? (floorCosts[nextFloorCount] ?? 0)
    : (floorCosts[floorCosts.length - 1] ?? 0) * Math.pow(5, nextFloorCount - floorCosts.length + 1);
  const canAfford = state.sp >= floorCost;

  addFloorEl.innerHTML = '';
  if (canBuildMore) {
    const addBtn = document.createElement('button');
    addBtn.className = 'add-floor-btn';
    addBtn.disabled = !canAfford;
    addBtn.textContent = t('add_floor_cost', { sp: formatSp(floorCost) });
    addBtn.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('buy-floor'));
    });
    addFloorEl.appendChild(addBtn);
  }
}

function renderFloorRoom(
  container: HTMLElement,
  floor: Floor,
  state: GameStateData,
  onRoomClick: RoomClickCallback,
): void {
  const room = floor.room;

  if (room === null) {
    // Empty floor - no room built
    container.classList.add('empty');
    const emptyBg = document.createElement('img');
    emptyBg.className = 'room-bg-img';
    emptyBg.src = getEmptyRoomImage(state);
    emptyBg.alt = '';
    emptyBg.draggable = false;
    container.appendChild(emptyBg);
    const buildText = document.createElement('div');
    buildText.className = 'room-name room-name-overlay';
    buildText.textContent = t('build_room');
    container.appendChild(buildText);
    container.addEventListener('click', () => onRoomClick(floor.level, null));
    return;
  }

  // Built room
  container.classList.add('built');

  // Room background image (theme-aware)
  const themeId = state.selectedHauntedBuildingId
    ? (state.hauntedBuildingMetas?.find(m => m.id === state.selectedHauntedBuildingId)?.themeId as string | undefined)
    : undefined;
  const roomBgSrc = getRoomBgImage(room.type, themeId);
  const roomBg = document.createElement('img');
  roomBg.className = 'room-bg-img';
  roomBg.src = roomBgSrc;
  roomBg.alt = '';
  roomBg.draggable = false;
  container.appendChild(roomBg);

  // Render props inside the room
  renderProps(container, room);

  // Check if visitors are on this floor (needed for effect visibility)
  const currentGroup = state.visitorGroup;
  const visitorOnFloor = currentGroup &&
    !currentGroup.done &&
    state.building[currentGroup.currentFloorIndex]?.level === floor.level;

  // Determine effect visibility class based on open/close state and phase
  const visitorPhase = (visitorOnFloor && currentGroup) ? currentGroup.phase : null;
  const effectVisibilityClass = state.isOpen
    ? (visitorOnFloor
        ? (visitorPhase === 'fog' || visitorPhase === 'scare' ? 'active' : '')
        : '')
    : 'preview';

  // Base ambient effect per room type (NO fog by default - only from installed props)
  const roomAmbientMap: Record<string, string> = {
    mirror_room: 'ambient-flicker',
    lab: 'ambient-flicker',
    doll_room: 'ambient-flicker',
    library: 'ambient-dust',
    kitchen: 'ambient-steam',
    bathroom: 'ambient-drip',
  };
  const ambientClass = roomAmbientMap[room.type];
  if (ambientClass) {
    const ambientDiv = document.createElement('div');
    ambientDiv.className = `room-effect room-effect-${ambientClass}`;
    if (effectVisibilityClass) ambientDiv.classList.add(effectVisibilityClass);
    container.appendChild(ambientDiv);
  }

  // Extra staging effects from installed props
  const stagingEffects = ['dry_ice', 'scent_sprayer', 'strobe_light', 'temp_controller'];
  for (const rp of room.props) {
    if (stagingEffects.includes(rp.propId)) {
      const effectDiv = document.createElement('div');
      effectDiv.className = `room-effect room-effect-${rp.propId}`;
      if (effectVisibilityClass) effectDiv.classList.add(effectVisibilityClass);
      container.appendChild(effectDiv);
    }
  }

  // Check ghost
  let ghost = null;
  if (room.assignedGhostId) {
    ghost = state.ghosts.find((g) => g.id === room.assignedGhostId) ?? null;
  }

  if (ghost) {
    container.classList.add('has-ghost');
    if (ghost.fatigue >= 100) {
      container.classList.add('fatigue-warning');
    }

    const ghostDef = GHOST_DEFS[ghost.characterId];

    const ghostImg = document.createElement('img');
    ghostImg.className = 'ghost-in-room ghost-img-room';
    ghostImg.draggable = false;

    if (visitorOnFloor && visitorPhase) {
      // Visitors are here: ghost visibility depends on phase
      if (visitorPhase === 'scare') {
        // Ghost pops out with scare animation
        ghostImg.src = ghostDef.scareImage;
        ghostImg.classList.add('ghost-phase-scare');
        container.classList.add('floor-shaking');
      } else if (visitorPhase === 'leaving') {
        // Ghost fading back into hiding
        ghostImg.src = ghostDef.scareImage;
        ghostImg.classList.add('ghost-phase-leaving');
      } else {
        // entering / fog: ghost is hidden (invisible)
        ghostImg.src = ghostDef.hideImage;
        ghostImg.classList.add('ghost-phase-hidden');
      }
    } else {
      // No visitors: show hide pose (semi-transparent)
      ghostImg.src = ghostDef.hideImage;
      ghostImg.classList.add('ghost-hiding');
    }
    ghostImg.alt = t(ghostDef.nameKey as Parameters<typeof t>[0]);
    container.appendChild(ghostImg);

    const ghostNameEl = document.createElement('div');
    ghostNameEl.className = 'ghost-name-in-room';
    ghostNameEl.textContent = t(ghostDef.nameKey as Parameters<typeof t>[0]);
    container.appendChild(ghostNameEl);

    // Fatigue indicator removed from room card (shown in ghost detail panel instead)
  } else {
    const noGhost = document.createElement('div');
    noGhost.className = 'room-name room-name-overlay';
    noGhost.textContent = t('no_ghost');
    container.appendChild(noGhost);
  }

  // Room name
  const roomNameEl = document.createElement('div');
  roomNameEl.className = 'room-name room-name-overlay';
  const roomDef = ROOM_TYPES[room.type];
  roomNameEl.textContent = t(roomDef.nameKey as Parameters<typeof t>[0]);
  container.appendChild(roomNameEl);

  // Upgrade badge
  if (room.upgradeLevel > 1) {
    const badge = document.createElement('div');
    badge.className = 'room-upgrade-badge';
    badge.textContent = `+${room.upgradeLevel - 1}`;
    container.appendChild(badge);
  }

  // Border color + overlays based on condition/hygiene
  const condVal = room.condition ?? 100;
  const hygVal = room.hygiene ?? 100;
  const minStat = Math.min(condVal, hygVal);

  if (minStat >= 60) {
    container.style.borderColor = '#2ecc71';
  } else if (minStat >= 30) {
    container.style.borderColor = '#f39c12';
  } else if (minStat > 0) {
    container.style.borderColor = '#e74c3c';
    container.classList.add('room-danger-pulse');
  }

  // Collapsed overlay (condition = 0)
  if (room.collapsed) {
    const collapseOverlay = document.createElement('div');
    collapseOverlay.className = 'room-collapsed-overlay';
    const collapseIcon = document.createElement('div');
    collapseIcon.className = 'room-collapsed-icon';
    collapseIcon.textContent = '!';
    collapseOverlay.appendChild(collapseIcon);
    const collapseText = document.createElement('div');
    collapseText.className = 'room-collapsed-text';
    collapseText.textContent = t('room_repair_needed');
    collapseOverlay.appendChild(collapseText);
    container.appendChild(collapseOverlay);
  }

  // Repair overlay
  if (room.underRepair && room.repairEndsAt > 0) {
    const repairOverlay = document.createElement('div');
    repairOverlay.className = 'room-repair-overlay';
    const stripes = document.createElement('div');
    stripes.className = 'repair-stripes';
    repairOverlay.appendChild(stripes);
    const repairText = document.createElement('div');
    repairText.className = 'repair-text';
    repairText.textContent = t('room_repairing');
    repairOverlay.appendChild(repairText);
    // Progress bar
    const totalTime = 30000;
    const elapsed = Date.now() - (room.repairEndsAt - totalTime);
    const progress = Math.min(100, Math.max(0, (elapsed / totalTime) * 100));
    const progressBar = document.createElement('div');
    progressBar.className = 'repair-progress-bar';
    const progressFill = document.createElement('div');
    progressFill.className = 'repair-progress-fill';
    progressFill.style.width = `${progress}%`;
    progressBar.appendChild(progressFill);
    repairOverlay.appendChild(progressBar);
    container.appendChild(repairOverlay);
  }

  // Cleaning overlay
  if (room.underCleaning && room.cleaningEndsAt > 0) {
    const cleanOverlay = document.createElement('div');
    cleanOverlay.className = 'room-repair-overlay room-cleaning-overlay';
    const stripes = document.createElement('div');
    stripes.className = 'cleaning-stripes';
    cleanOverlay.appendChild(stripes);
    const cleanText = document.createElement('div');
    cleanText.className = 'repair-text cleaning-text';
    cleanText.textContent = t('room_cleaning');
    cleanOverlay.appendChild(cleanText);
    const totalTime = 20000;
    const elapsed = Date.now() - (room.cleaningEndsAt - totalTime);
    const progress = Math.min(100, Math.max(0, (elapsed / totalTime) * 100));
    const progressBar = document.createElement('div');
    progressBar.className = 'repair-progress-bar';
    const progressFill = document.createElement('div');
    progressFill.className = 'repair-progress-fill cleaning-fill';
    progressFill.style.width = `${progress}%`;
    progressBar.appendChild(progressFill);
    cleanOverlay.appendChild(progressBar);
    container.appendChild(cleanOverlay);
  }

  // NOTE: Visitors are rendered by updateVisitors() separately to preserve DOM across re-renders.

  container.addEventListener('click', () => onRoomClick(floor.level, room));
}

/**
 * Calculate visitor left position (%) based on phase and elapsed time.
 * JS-driven positioning so DOM rebuilds do not reset animation state.
 */
function calcVisitorLeft(phase: string, phaseElapsedMs: number, visitorIdx: number, reaction?: VisitorReaction | null): number {
  const stagger = visitorIdx * 8;
  switch (phase) {
    case 'entering': {
      // 2.5s: -10% -> 35%
      const progress = Math.min(phaseElapsedMs / 2500, 1);
      return -10 + progress * 45 + stagger;
    }
    case 'fog':
    case 'scare':
      return 35 + stagger;
    case 'leaving': {
      // Speed depends on reaction:
      // scared/excited → fast exit (0.8s)
      // bored → slow exit (2.5s)
      // normal/angry → normal (1.5s)
      let duration = 1500;
      if (reaction === 'scared' || reaction === 'excited') duration = 800;
      else if (reaction === 'bored') duration = 2500;
      const progress = Math.min(phaseElapsedMs / duration, 1);
      return 35 + progress * 75 + stagger;
    }
    default:
      return -20;
  }
}

/** Get emoji for visitor reaction */
function getReactionText(reaction: VisitorReaction): string {
  switch (reaction) {
    case 'scared': return '\u{1F631}';   // scream face
    case 'bored': return '\u{1F971}';    // yawning face
    case 'angry': return '\u{1F620}';    // angry face
    case 'excited': return '\u{1F929}';  // star-struck face
    default: return '';
  }
}

/**
 * Render prop images at their fixed positions inside the room.
 */
function renderProps(container: HTMLElement, room: Room): void {
  for (const roomProp of room.props) {
    const propId = roomProp.propId as string;
    const imgSrc = PROP_IMAGES[propId];
    const pos = PROP_POSITIONS[propId];
    if (!imgSrc || !pos) continue;

    const propImg = document.createElement('img');
    propImg.className = 'room-prop-img';
    propImg.src = imgSrc;
    propImg.alt = '';
    propImg.draggable = false;
    propImg.decoding = 'async';

    // Apply positioning styles
    propImg.style.position = 'absolute';
    propImg.style.width = pos.width;
    propImg.style.zIndex = '1';
    if (pos.top !== undefined) propImg.style.top = pos.top;
    if (pos.bottom !== undefined) propImg.style.bottom = pos.bottom;
    if (pos.left !== undefined) propImg.style.left = pos.left;
    if (pos.right !== undefined) propImg.style.right = pos.right;
    if (pos.transform) propImg.style.transform = pos.transform;
    if (pos.opacity) propImg.style.opacity = pos.opacity;

    // Scale prop slightly based on level (1 = normal, 2 = 1.15x, 3 = 1.3x)
    const levelScale = 1 + (roomProp.level - 1) * 0.15;
    if (levelScale !== 1) {
      const existingTransform = pos.transform ?? '';
      propImg.style.transform = `${existingTransform} scale(${levelScale})`.trim();
    }

    container.appendChild(propImg);
  }
}

/** Get localized text for a story role */
function getStoryRoleText(role: StoryRole): string {
  switch (role) {
    case 'intro': return t('story_intro');
    case 'rising': return t('story_rising');
    case 'climax': return t('story_climax');
    case 'resolution': return t('story_resolution');
    default: return '';
  }
}

let _prevVisitorQueueRef: GameStateData['visitorGroup'] | null = null;

export function renderVisitorQueue(
  container: HTMLElement,
  state: GameStateData,
): void {
  if (_prevVisitorQueueRef === state.visitorGroup && container.childElementCount > 0) return;
  _prevVisitorQueueRef = state.visitorGroup;

  container.innerHTML = '';
  if (!state.visitorGroup || state.visitorGroup.done) return;

  const group = state.visitorGroup;
  for (const visitor of group.visitors) {
    const token = document.createElement('div');
    token.className = `visitor-token ${visitor.type}`;

    // Add walking animation when group is moving
    if (!visitor.fled) {
      token.classList.add('visitor-walking');
    }
    if (visitor.fled) {
      token.classList.add('visitor-fled');
    }

    // Use sprite sheet thumbnail instead of individual images
    const spriteThumb = document.createElement('div');
    spriteThumb.className = 'visitor-sprite-thumb';
    token.appendChild(spriteThumb);

    container.appendChild(token);
  }
}

// ── Persistent Visitor DOM Management ─────────────────────

/**
 * Module-level tracking of currently rendered visitor sprite elements.
 * These persist across renderBuilding() calls to avoid DOM rebuilds
 * that would reset CSS animations.
 */
let _visitorElements: Map<string, HTMLDivElement> = new Map();
let _visitorFloorLevel = -1;
let _visitorGroupId = '';
let _spriteFrameInterval: ReturnType<typeof setInterval> | null = null;
let _spriteFrame = 0;

// Sprite sheet: 880x1184, 3cols x 4rows
// Display size: 40x52, bg-size: 120x208
// Row 2 (right-facing) Y = -104px, frames at X = 0, -40, -80
// Row 0 (front-facing) Y = 0, center frame X = -40
const SPRITE_FRAME_W = 40;
const SPRITE_RIGHT_Y = -104; // Row 2 (right-facing)
const SPRITE_FRONT_Y = 0;   // Row 0 (front-facing)
const SPRITE_FRAMES = [0, -40, -80]; // 3 frame X positions

function startSpriteAnimation(): void {
  if (_spriteFrameInterval) return;
  _spriteFrame = 0;
  _spriteFrameInterval = setInterval(() => {
    _spriteFrame = (_spriteFrame + 1) % 3;
    _visitorElements.forEach((el) => {
      const isWalking = el.dataset.spriteMode === 'walk' || el.dataset.spriteMode === 'run';
      if (isWalking) {
        const x = SPRITE_FRAMES[_spriteFrame];
        el.style.backgroundPosition = `${x}px ${SPRITE_RIGHT_Y}px`;
      }
    });
  }, 180); // ~5.5fps per frame, 3 frames = ~1.8 walks/sec
}

function stopSpriteAnimation(): void {
  if (_spriteFrameInterval) {
    clearInterval(_spriteFrameInterval);
    _spriteFrameInterval = null;
  }
}

/**
 * Update visitor sprites in the building view.
 * This function manages visitor DOM elements independently from renderBuilding(),
 * so that innerHTML = '' in the floor re-render does not destroy visitor animations.
 *
 * Call this AFTER renderBuilding() in the render loop.
 */
export function updateVisitors(
  buildingContainer: HTMLElement,
  state: GameStateData,
): void {
  const group = state.visitorGroup;

  // No visitor group or group is done: clean up all sprites
  if (!group || group.done) {
    _visitorElements.forEach((el) => el.remove());
    _visitorElements.clear();
    _visitorFloorLevel = -1;
    _visitorGroupId = '';
    stopSpriteAnimation();
    return;
  }

  const currentFloor = state.building[group.currentFloorIndex];
  if (!currentFloor) return;
  const floorLevel = currentFloor.level;
  const phase = group.phase;
  const now = Date.now();
  const phaseElapsed = now - (group.phaseStartedAt || now);

  // If group changed or floor changed, recreate all visitor sprites
  const needsRecreate =
    group.id !== _visitorGroupId ||
    floorLevel !== _visitorFloorLevel;

  if (needsRecreate) {
    // Remove old sprites
    _visitorElements.forEach((el) => el.remove());
    _visitorElements.clear();
    _visitorFloorLevel = floorLevel;
    _visitorGroupId = group.id;

    // Find the room DOM for this floor
    const floorRoom = buildingContainer.querySelector<HTMLElement>(
      `#floor-${floorLevel} .floor-room`,
    );
    if (!floorRoom) return;

    // Create sprite elements for each visitor
    group.visitors.forEach((visitor, idx) => {
      const el = document.createElement('div');
      el.className = 'visitor-sprite walking';
      el.style.left = '-10%';
      el.dataset.visitorIdx = String(idx);
      floorRoom.appendChild(el);
      _visitorElements.set(visitor.id || String(idx), el);
    });
  }

  // Ensure sprites are parented to the correct floor-room
  // (renderBuilding may have rebuilt the floor DOM, but we re-attached via preservedVisitors)
  const floorRoom = buildingContainer.querySelector<HTMLElement>(
    `#floor-${floorLevel} .floor-room`,
  );

  // Update each visitor sprite based on current phase
  let idx = 0;
  for (const [, el] of _visitorElements) {
    const visitor = group.visitors[idx];
    if (!visitor) { idx++; continue; }

    // Ensure element is in the DOM (may have been detached by floor rebuild)
    if (!el.parentElement && floorRoom) {
      floorRoom.appendChild(el);
    }

    // Calculate JS-interpolated position
    const reaction = visitor.currentReaction;
    const leftPos = calcVisitorLeft(phase, phaseElapsed, idx, reaction);
    el.style.left = `${leftPos}%`;

    // Set sprite mode (JS controls background-position, not CSS animation)
    switch (phase) {
      case 'entering':
        el.dataset.spriteMode = 'walk';
        el.classList.remove('visitor-scared', 'visitor-shaking', 'visitor-excited');
        if (!el.classList.contains('visitor-bounce-in')) {
          el.classList.add('visitor-bounce-in');
          setTimeout(() => el.classList.remove('visitor-bounce-in'), 500);
        }
        _removeReactionBubble(el);
        break;
      case 'fog':
        el.dataset.spriteMode = 'stand';
        el.style.backgroundPosition = `${-SPRITE_FRAME_W}px ${SPRITE_FRONT_Y}px`;
        el.classList.remove('visitor-scared', 'visitor-shaking', 'visitor-excited');
        _removeReactionBubble(el);
        break;
      case 'scare':
        el.dataset.spriteMode = 'stand';
        el.style.backgroundPosition = `${-SPRITE_FRAME_W}px ${SPRITE_FRONT_Y}px`;
        if (reaction === 'scared') {
          el.classList.add('visitor-scared', 'visitor-shaking');
          el.classList.remove('visitor-excited');
        } else if (reaction === 'excited') {
          el.classList.add('visitor-excited');
          el.classList.remove('visitor-scared', 'visitor-shaking');
        } else {
          el.classList.remove('visitor-scared', 'visitor-shaking', 'visitor-excited');
        }
        if (reaction && reaction !== 'normal') {
          _ensureReactionBubble(el, reaction, idx);
        }
        break;
      case 'leaving':
        if (reaction === 'scared' || reaction === 'excited' || visitor.fled) {
          el.dataset.spriteMode = 'run';
        } else {
          el.dataset.spriteMode = 'walk';
        }
        if (reaction && reaction !== 'normal') {
          _ensureReactionBubble(el, reaction, idx);
        }
        break;
    }
    // Ensure sprite frame interval is running
    startSpriteAnimation();

    idx++;
  }
}

/** Add a reaction bubble as a child of the sprite element if not already present */
function _ensureReactionBubble(
  spriteEl: HTMLDivElement,
  reaction: VisitorReaction,
  visitorIdx: number,
): void {
  if (!reaction) return;
  let bubble = spriteEl.querySelector<HTMLElement>('.reaction-bubble');
  if (bubble) {
    // Update reaction type if changed
    const currentReaction = bubble.dataset.reaction;
    if (currentReaction === reaction) return;
    bubble.remove();
  }
  bubble = document.createElement('div');
  bubble.className = `reaction-bubble reaction-${reaction}`;
  bubble.dataset.reaction = reaction;
  bubble.textContent = getReactionText(reaction);
  bubble.style.animationDelay = `${visitorIdx * 0.15}s`;
  spriteEl.appendChild(bubble);
}

/** Remove reaction bubble from sprite element */
function _removeReactionBubble(spriteEl: HTMLDivElement): void {
  const bubble = spriteEl.querySelector('.reaction-bubble');
  if (bubble) bubble.remove();
}
